<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const uploadState = ref('idle');

const standardVariables = [
  '{{ contact.name }}',
  '{{ contact.first_name }}',
  '{{ contact.last_name }}',
  '{{ contact.phone_number }}',
  '{{ contact.email }}',
  '{{ contact.city }}',
  '{{ contact.country }}',
];

const lastFocusedVariable = ref(null);

const insertVariable = (variable) => {
  if (!processedParams.value.body) return;
  
  if (lastFocusedVariable.value !== null) {
    processedParams.value.body[lastFocusedVariable.value] = variable;
  } else {
    // Si no hay ninguno enfocado, llenamos el primero que esté vacío
    const keys = Object.keys(processedParams.value.body);
    const firstEmpty = keys.find(k => !processedParams.value.body[k]);
    if (firstEmpty) {
      processedParams.value.body[firstEmpty] = variable;
    } else if (keys.length > 0) {
      processedParams.value.body[keys[0]] = variable;
    }
  }
};

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  uploadState.value = 'processing';
  try {
    const response = await uploadFile(file);
    updateMediaUrl(response.fileUrl);
    uploadState.value = 'uploaded';
  } catch (error) {
    uploadState.value = 'failed';
    useAlert('Error al subir el archivo');
  }
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  uploadState,
  handleFileUpload,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
  isFormInvalid,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex flex-col gap-1 mb-2.5">
          <div class="flex items-center gap-2">
            <label class="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-n-alpha-black2 hover:bg-n-alpha-3 border border-n-strong rounded-md transition-colors" :class="{ 'opacity-50 pointer-events-none': uploadState === 'processing' }">
              <span v-if="uploadState === 'processing'" class="i-lucide-loader-2 animate-spin w-4 h-4"></span>
              <span v-else-if="uploadState === 'uploaded'" class="i-lucide-check text-n-teal-9 w-4 h-4"></span>
              <span v-else class="i-lucide-upload w-4 h-4"></span>
              {{ uploadState === 'processing' ? 'Subiendo...' : uploadState === 'uploaded' ? 'Archivo subido' : 'Subir archivo' }}
              <input type="file" class="hidden" @change="handleFileUpload" />
            </label>
            <span v-if="processedParams.header?.media_url" class="text-xs text-n-slate-11 truncate max-w-xs">
              {{ processedParams.header.media_url.split('/').pop() }}
            </span>
          </div>
          <p class="text-xs text-n-slate-11 mt-1">
            Meta requiere que adjuntes el archivo al momento del envío. El archivo adjuntado al crear la plantilla es solo una muestra para su aprobación.
          </p>
          <p v-if="!processedParams.header?.media_url" class="text-xs text-n-ruby-9 font-medium mt-1">
            Requerido: Debes adjuntar un archivo multimedia para enviar esta campaña.
          </p>
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            :message="!processedParams.header?.media_name ? 'Requerido: Ingresa el nombre del documento.' : ''"
            :message-type="!processedParams.header?.media_name ? 'error' : ''"
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
            :message="!processedParams.body[key] ? 'Requerido: Debes proporcionar un valor para esta variable.' : ''"
            :message-type="!processedParams.body[key] ? 'error' : ''"
            @focus="lastFocusedVariable = key"
          />
        </div>
        
        <div class="mt-2 p-3 bg-n-alpha-black2 rounded-md">
          <p class="text-xs font-medium text-n-slate-12 mb-2">Variables estándar disponibles (Clic para insertar en el campo seleccionado)</p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="v in standardVariables"
              :key="v"
              type="button"
              class="px-2 py-1 text-xs font-mono bg-n-alpha-2 border border-n-strong rounded-md hover:bg-n-alpha-3 transition-colors text-n-slate-11 cursor-pointer"
              @click="insertVariable(v)"
            >
              {{ v }}
            </button>
          </div>
          <p class="text-[11px] text-n-slate-11 mt-2 mb-0">También puedes usar atributos personalizados: <code v-pre class="bg-n-alpha-2 px-1 rounded">{{ contact.custom_attributes.tu_atributo }}</code></p>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
            :message="!processedParams.buttons[index].parameter ? 'Requerido: Debes proporcionar un parámetro para el botón.' : ''"
            :message-type="!processedParams.buttons[index].parameter ? 'error' : ''"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
